import React from "react"
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import CCOCard from "../../fundamentals/CCOCard";
import CCOTextOverlay from "../../fundamentals/CCOTextOverlay";
import {metaDataProperty} from "../../preview/previewMetaData";
import PreviewElementBoxDrawer from "../../preview/PreviewElementBoxDrawer";
import {usePreviewContext} from "../../../context/PreviewContext";
import {getAltText, getPictureUri} from "../../fundamentals/CCOPicture";
import {useTranslation} from "react-i18next";
import CCOLinkText, {textDecorationStyle} from "../../fundamentals/CCOLinkText";
import {SxProps, Theme, useTheme} from "@mui/material/styles";
import {SosTeaserType} from "../../types";
import CCOAiGeneratedImageLabel, {useAiGeneratedImageLabel} from "../../fundamentals/CCOAiGeneratedImageLabel";

interface Props {
    item: SosTeaserType,
    orientation: string,
    teaser?: boolean
    useSquareImage?: boolean
    customStyle?: SxProps<Theme>
    shouldUseResponsiveFontSize?: boolean
    showAiGeneratedImageLabel?: boolean
    teasercardLocation: string
}

function C021KachelTeaserCard(props: Props) {
    const {t} = useTranslation();
    const theme = useTheme();
    const isPreview = usePreviewContext().isPreview;

    const width =
        (props.orientation === 'portrait_ratio1x1' ? 890 :
            (props.orientation === 'portrait_ratio3x4') ? 780 : 2080);

    const imgWidth = props.useSquareImage ? 540 : width;

    const kachelTeaserCardFocusedStyle = {
        paddingX: '2px',
        '& a:focus-visible': {
            outlineOffset: 0,
            outline: '2px solid black',
        },
    }

    const cdnAwareSrc = getPictureUri(String(imgWidth), isPreview, props.item?.picture, props.orientation);

    const isColorTile = !!(props?.item?.teaserSettings?.color || !props.item?.picture?.data?.uri);
    const pictureAlt = getAltText(props.item?.picture?.alt, props.item?.picture?.title);
    const aiLabel = useAiGeneratedImageLabel(
        props.showAiGeneratedImageLabel && !isColorTile ? props.item?.picture : undefined, pictureAlt);

    const bgImage = isColorTile ? {
        src: `/empty${imgWidth}.svg`,
        alt: pictureAlt,
        color: props.item.teaserSettings?.color,
        width: imgWidth,
        isColor: true
    } : {
        src: cdnAwareSrc ?? `/media${props.item?.picture?.data?.uri}/${props.orientation}/${imgWidth}`,
        alt: aiLabel?.imageAlt ?? pictureAlt,
        width: imgWidth,
        isColor: false
    }

    const fontSize = props.shouldUseResponsiveFontSize
        ? {xs: '1.4rem', md: '1rem', lg: '1.4rem'}
        : '1.4rem';
    const compactHeadlineBreakpoint = 865;

    const cta = (props.item?.teaserCallToActionSettings);
    const showCTA = !!(cta?.callToActionEnabled && props.item?.teaserTarget);

    const itemWrapSx = {
        width: 'fit-content',
        display: 'block',
        alignSelf: 'flex-start',
    } as const;

    const itemType = props.item?.type;
    const isSosTeaser = itemType === 'SOSTeaser';
    const rooflineValue = isSosTeaser ? (props.item?.headline || props.item?.title) : props.item?.teaserTitle;
    const rooflineProperty = isSosTeaser
        ? (props.item?.headline ? 'properties.headline' : 'properties.title')
        : 'properties.teaserTitle';
    const headlineValue = isSosTeaser ? props.item?.teaserTitle : (props.item?.headline || props.item?.title);
    const headlineProperty = isSosTeaser
        ? 'properties.teaserTitle'
        : (props.item?.headline ? 'properties.headline' : 'properties.title');

    return (
        <CCOCard
            item={props.item}
            noSize={!props.teaser}
            customStyle={{...props.customStyle, ...kachelTeaserCardFocusedStyle}}
            teasercardLocation={props.teasercardLocation}
        >
            <React.Fragment>
                <CCOTextOverlay
                    image={bgImage}
                    imageOverlay={aiLabel && <CCOAiGeneratedImageLabel label={aiLabel} variant='textOverlayTile'/>}
                    color={props.item.teaserSettings?.color ? props.item.teaserSettings?.color :
                        'green'}
                >
                    <Grid container size={{xs: 12}}>
                        <Grid sx={{mx: 6}}>
                            <PreviewElementBoxDrawer propertyId={props.item.id}>

                                <Stack
                                    sx={{alignItems: 'flex-start'}}
                                    spacing={{xs: 3, sm: 3.5, lg: 4}}
                                >
                                    {/* Roofline */}
                                    {rooflineValue ? (
                                        <Box sx={itemWrapSx}>
                                            <Typography
                                                variant='overline'
                                                component="span"
                                                sx={{
                                                    display: 'inline-block',
                                                    background: 'white',
                                                    boxDecorationBreak: 'clone',
                                                    py: '8px',
                                                    px: '12px',
                                                    width: 'fit-content',
                                                }}
                                                {...metaDataProperty(isPreview,
                                                    rooflineProperty,
                                                    props.item.id)}
                                            >
                                                {rooflineValue}
                                            </Typography>
                                        </Box>
                                    ) : null}

                                    {/* Headline */}
                                    {headlineValue ? (
                                        <Box sx={itemWrapSx}>
                                            <Typography
                                                variant='h3'
                                                component='div'
                                                sx={{
                                                    m: 0,
                                                    p: 0,
                                                    display: 'block',
                                                    width: 'fit-content',
                                                    fontSize: fontSize,
                                                    lineHeight: {xs: '160% !important', lg: '120%'},
                                                    [theme.breakpoints.between(
                                                        'md',
                                                        compactHeadlineBreakpoint
                                                    )]: {
                                                        fontSize: '0.9rem !important',
                                                    },
                                                    [theme.breakpoints.between(
                                                        compactHeadlineBreakpoint,
                                                        'lg'
                                                    )]: {
                                                        fontSize: '1rem !important',
                                                    },
                                                }}
                                            >
                                                <Box
                                                    component="span"
                                                    sx={{
                                                        background: 'white',
                                                        boxDecorationBreak: 'clone',
                                                        py: {xs: '8px', lg: '10px'},
                                                        px: '12px',
                                                        display: 'inline',
                                                        verticalAlign: 'top',
                                                    }}
                                                    {...metaDataProperty(
                                                        isPreview,
                                                        headlineProperty,
                                                        props.item.id
                                                    )}
                                                >
                                                    {headlineValue}
                                                </Box>
                                            </Typography>
                                        </Box>
                                    ) : null}

                                    {/* CTA */}
                                    {showCTA ? (
                                        <Box sx={itemWrapSx}>
                                            <Typography
                                                component='span'
                                                sx={{
                                                    fontSize: '1rem',
                                                    background: 'white',
                                                    boxDecorationBreak: 'clone',
                                                    py: '8px',
                                                    px: '12px',
                                                    display: 'inline-block',
                                                    '&:hover': {
                                                        ...textDecorationStyle,
                                                        textDecorationColor: theme.palette.primary.main,
                                                        cursor: 'pointer',
                                                    }
                                                }}
                                            >
                                                <CCOLinkText hasFocusedStyle displayContents>
                                                    <>{(cta?.callToActionCustomText) ??
                                                        t('c011_learn_more')}</>
                                                </CCOLinkText>
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Stack>

                            </PreviewElementBoxDrawer>
                        </Grid>
                    </Grid>
                </CCOTextOverlay>
            </React.Fragment>
        </CCOCard>
    )
}

export default C021KachelTeaserCard
